import api from 'services/api';
import { array, enforce, nullable, num, numeric, object, runSchema, str, withDefault } from 'validation';

import { retentionPolicySchema, type RetentionPolicy } from 'api/shared/retentionPolicy';
import { filterConfigSchema, listColumnConfigSchema, type FilterConfig, type ListColumnConfig } from 'api/shared/listConfig';

// GET /zones — the list read. Backend: routes/zones.js `GET /zones` → `getZones`
// (modules/generic_functions/zones/getZones.js) for the paginated rows, wrapped
// as `{ zones, pagination }`, then the route adds `{ config, filtersConfig }`.
//
// `ZoneRow` is a SEPARATE type from the POST/PUT `Zone` (getZoneDetails), even
// though the columns match today: two independent queries (equal-by-coincidence,
// not identity — see ../../README.md).
export interface ZoneRow {
  readonly zone_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly retention_policies: readonly RetentionPolicy[];
}

interface Pagination {
  readonly total_records: number;
  readonly total_pages: number;
  readonly current_page: number;
  readonly limit: number;
  readonly offset: number;
}

export interface ZonesPage {
  readonly zones: readonly ZoneRow[];
  readonly pagination: Pagination;
  // List-table config, shared across list endpoints (see api/shared/listConfig).
  readonly config: readonly ListColumnConfig[];
  readonly filtersConfig: readonly FilterConfig[];
}

const rowSchema = object<ZoneRow>({
  zone_id: num(),
  name: str(),
  description: nullable(str()),
  created_at: str(),
  updated_at: str(),
  // NOT NULL DEFAULT '[]' on the wire; withDefault also absorbs a legacy
  // object-format value (dropped to []) rather than failing the row.
  retention_policies: withDefault(array(retentionPolicySchema), []),
});

// COUNT-derived totals can arrive as numeric strings from Postgres.
const paginationSchema = object<Pagination>({
  total_records: numeric(),
  total_pages: numeric(),
  current_page: numeric(),
  limit: numeric(),
  offset: numeric(),
});

const pageSchema = object<ZonesPage>({
  zones: array(rowSchema),
  pagination: paginationSchema,
  config: array(listColumnConfigSchema),
  filtersConfig: array(filterConfigSchema),
});

/**
 * GET `/zones`. Returns the full list page (rows + pagination + list config).
 * Throws on transport error or structural validation failure of the response.
 */
export const getZones = async (params: Record<string, unknown> = {}): Promise<ZonesPage> => {
  const res = await api.get('/zones', { params });
  const r = runSchema(pageSchema, res.data, 'getZones');
  if (!r.ok) {
    throw new Error('getZones: response failed validation');
  }
  return enforce(r.value, r.warnings, ['zones[*].zone_id']);
};
